import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import '../styles/Leave.css';

const STATUS_LABELS = {
  not_due: 'Not due yet',
  due: 'Not yet paid',
  prevented: 'Not yet paid',
  paid: 'Already paid, no voucher',
  historical: 'Past year',
};

const apiBase = () => `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/leave`;

const LeaveBalances = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('/leave-balances', 'Edit');
  const [people, setPeople] = useState([]);
  const [balances, setBalances] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState('balances');
  const [nameQuery, setNameQuery] = useState('');

  const load = async () => {
    const response = await axios.get(`${apiBase()}/setup`);
    setPeople(response.data?.people || []);
    setBalances(response.data?.balances || []);
    setLoaded(true);
  };

  useEffect(() => {
    load().catch((err) => {
      console.error(err);
      setError('Could not load leave balances.');
    });
  }, []);

  const initialize = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await axios.post(`${apiBase()}/balances/initialize`, {});
      const created = Number(response.data.created_count) || 0;
      setMessage(created
        ? `Opening balances added for ${created} leave year row(s).`
        : 'No new rows. Opening balances are only created once. A person who already has leave years is skipped.');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not calculate opening balances.');
    } finally {
      setBusy(false);
    }
  };

  const saveRow = async (balance, extra = {}) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const used = drafts[balance.balance_id];
      await axios.put(`${apiBase()}/balances/${balance.balance_id}`, {
        used_days: used == null || used === '' ? undefined : used,
        ...extra,
      });
      setMessage('Balance updated.');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update this balance.');
    } finally {
      setBusy(false);
    }
  };

  const closeAsPaid = async (balance) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await axios.post(`${apiBase()}/balances/${balance.balance_id}/close-allowance`, {});
      setMessage(`${balance.Employee?.employee_name}: the year starting ${String(balance.leave_year_start).slice(0, 10)} is already paid. No voucher will be issued.`);
      setConfirmClose(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not mark this year as paid.');
    } finally {
      setBusy(false);
    }
  };

  const setHandling = async (person, handledManually) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await axios.put(`${apiBase()}/setup/${person.employee_id}`, { handled_manually: handledManually });
      setMessage(handledManually
        ? `${person.employee_name} is paid by hand and is not in the leave system.`
        : `${person.employee_name} is in the leave system.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update this person.');
    } finally {
      setBusy(false);
    }
  };

  const query = nameQuery.trim().toLowerCase();
  const shownPeople = people.filter((person) => !query || person.employee_name.toLowerCase().includes(query));
  const shownBalances = balances.filter((balance) => (
    !query || (balance.Employee?.employee_name || '').toLowerCase().includes(query)
  ));

  return (
    <div className="leave-page">
      <h2>Leave Balances</h2>
      <p className="leave-note">Set the days already taken before this system started.</p>
      {error && <p className="leave-error">{error}</p>}
      {message && <p className="leave-ok">{message}</p>}
      {!loaded && !error && <p>Loading...</p>}
      {loaded && (<>
      <div className="leave-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'balances'}
          className={`leave-tab${tab === 'balances' ? ' active' : ''}`}
          onClick={() => setTab('balances')}
        >
          Opening balances ({balances.length})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'people'}
          className={`leave-tab${tab === 'people' ? ' active' : ''}`}
          onClick={() => setTab('people')}
        >
          People ({people.length})
        </button>
      </div>
      <input
        className="leave-search"
        type="search"
        placeholder="Find a name"
        value={nameQuery}
        onChange={(event) => setNameQuery(event.target.value)}
        aria-label="Find a name"
      />

      {tab === 'balances' && (
      <section className="leave-section">
        {canEdit && (
          <p>
            <button type="button" onClick={initialize} disabled={busy}>Calculate opening balances</button>
          </p>
        )}
        <table>
          <thead>
            <tr>
              <th>Employee</th>
              <th>Leave year</th>
              <th>Entitled days</th>
              <th>Days already taken</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {shownBalances.map((balance) => {
              const locked = balance.opening_locked || balance.payment_id || balance.allowance_status === 'paid' || balance.has_voucher;
              const canMarkPaid = balance.current_year
                && !balance.has_voucher
                && balance.allowance_status !== 'paid'
                && Number(balance.entitled_days) > 0;
              return (
                <tr key={balance.balance_id}>
                  <td>{balance.Employee?.employee_name}</td>
                  <td>{String(balance.leave_year_start).slice(0, 10)}</td>
                  <td>{balance.entitled_days}</td>
                  <td>
                    {canEdit && !locked ? (
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        defaultValue={balance.used_days}
                        onChange={(event) => setDrafts((current) => ({
                          ...current,
                          [balance.balance_id]: event.target.value,
                        }))}
                      />
                    ) : balance.used_days}
                  </td>
                  <td>{balance.has_voucher ? 'Paid' : (STATUS_LABELS[balance.allowance_status] || balance.allowance_status)}</td>
                  <td>
                    <div className="leave-actions">
                      {canEdit && !locked && (
                        <button type="button" disabled={busy} onClick={() => saveRow(balance)}>Save days</button>
                      )}
                      {canEdit && canMarkPaid && (
                        <button type="button" className="secondary" disabled={busy} onClick={() => setConfirmClose(balance)}>
                          Already paid, no voucher
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {shownBalances.length === 0 && (
              <tr>
                <td colSpan="6">{balances.length === 0 ? 'No balances yet. Calculate opening balances first.' : 'No matching names.'}</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
      )}

      {tab === 'people' && (
      <section className="leave-section">
        <p className="leave-note">Paid by hand means this person is not in the leave system.</p>
        <table>
          <thead>
            <tr>
              <th>Employee</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {shownPeople.map((person) => (
              <tr key={person.employee_id}>
                <td>{person.employee_name}</td>
                <td>
                  {person.handled_manually ? 'Paid by hand' : 'In the system'}
                  {person.nassit_exempt ? '. NASSIT exempt' : ''}
                </td>
                <td>
                  {canEdit && (
                    person.handled_manually ? (
                      <button type="button" className="secondary" disabled={busy} onClick={() => setHandling(person, false)}>
                        Put in the system
                      </button>
                    ) : (
                      <button type="button" className="secondary" disabled={busy} onClick={() => setHandling(person, true)}>
                        Handle manually
                      </button>
                    )
                  )}
                </td>
              </tr>
            ))}
            {shownPeople.length === 0 && (
              <tr>
                <td colSpan="3">{people.length === 0 ? 'No active employees.' : 'No matching names.'}</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
      )}
      </>)}
      {confirmClose && (
        <div className="leave-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="leave-close-title">
          <div className="leave-confirm">
            <h3 id="leave-close-title">Already paid, no voucher</h3>
            <p>
              {confirmClose.Employee?.employee_name}, year starting {String(confirmClose.leave_year_start).slice(0, 10)}, was already paid.
            </p>
            <p>Leave days still count. No voucher will be issued for this year.</p>
            {error && <p className="leave-error">{error}</p>}
            <div className="leave-actions">
              <button type="button" className="secondary" disabled={busy} onClick={() => setConfirmClose(null)}>Cancel</button>
              <button type="button" disabled={busy} onClick={() => closeAsPaid(confirmClose)}>Already paid, no voucher</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveBalances;
